/**
 * Fair Home V2 — wheel arbitration between the pager and the slides' own
 * scrolling.
 *
 * Cooldown: a trackpad flick's momentum outlives the ~520ms pager transition,
 * and its tail is jagged (… 6 6 10 …, … 32 168 25 …). Swiper pages again on any
 * delta that grows, so one swipe used to skip a section. Telling momentum from
 * a new swipe by the shape of the stream proved fragile on real Mac logs; a flat
 * cooldown does not: for `PAGE_COOLDOWN_MS` after a page turn the wheel never
 * reaches Swiper. Momentum on macOS dies down well within it, and deliberate
 * swipes in the tester logs came ≥1.1s apart.
 *
 * FE-004: on short viewports (`@media (max-height: 860px)`, see
 * `fair-home-v2.css`) a slide the slide-fit zoom could not fit becomes
 * scrollable. The wheel is kept from Swiper while the slide has room left to
 * scroll in that direction; a stream that was scrolling the slide does not page
 * on reaching the edge — the next swipe does.
 */
import { onBeforeUnmount, onMounted, watch, type Ref } from 'vue'

/** Sub-pixel slack: scrollHeight/clientHeight rounding must not fake an edge. */
const EDGE_EPSILON_PX = 1

/** After a page turn the wheel is ignored this long (transition ~520ms + momentum). */
const PAGE_COOLDOWN_MS = 1000

/** A pause longer than this between wheel events ends a swipe (Swiper uses 150ms too). */
const SWIPE_GAP_MS = 150

/** Slides overflowing by less than this are nudged, not read — their swipe may still page. */
const LATCH_MIN_OVERFLOW_PX = 64

export function useFairHomeV2SlideScroll(
  rootRef: Ref<HTMLElement | null>,
  activeSlide: Ref<number>,
) {
  let pagedAt = -Infinity
  let slideScrolledAt = -Infinity

  // Any page turn (wheel, drag, keys) starts the cooldown.
  watch(
    activeSlide,
    () => {
      pagedAt = performance.now()
    },
    { flush: 'sync' },
  )

  function onWheelCapture(event: WheelEvent) {
    // Native scrolling stays in every branch below — only Swiper is kept out.
    if (event.timeStamp - pagedAt < PAGE_COOLDOWN_MS) {
      event.stopPropagation()
      return
    }

    const target = event.target as HTMLElement | null
    const scrollport = target?.closest<HTMLElement>('.fair-home-v2__slide-inner')
    if (!scrollport) return

    const overflow = scrollport.scrollHeight - scrollport.clientHeight
    if (overflow <= EDGE_EPSILON_PX) return

    const goingDown = event.deltaY > 0
    const atTop = scrollport.scrollTop <= EDGE_EPSILON_PX
    const atBottom = scrollport.scrollTop >= overflow - EDGE_EPSILON_PX
    const sameSwipe = event.timeStamp - slideScrolledAt <= SWIPE_GAP_MS

    // At the edge the pager pages — unless this very swipe was scrolling the slide.
    if ((goingDown ? atBottom : atTop) && !sameSwipe) return

    if (overflow >= LATCH_MIN_OVERFLOW_PX) slideScrolledAt = event.timeStamp
    event.stopPropagation()
  }

  onMounted(() => {
    rootRef.value?.addEventListener('wheel', onWheelCapture, {
      capture: true,
      passive: true,
    })
  })

  onBeforeUnmount(() => {
    rootRef.value?.removeEventListener('wheel', onWheelCapture, {
      capture: true,
    } as EventListenerOptions)
  })
}
